import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { Lang } from '@app/shared';
import { translate, translateWith, type UiKey } from './dict';
import { describeApiError, type ApiFailureLike, type DescribedError } from './errors';

/**
 * Язык интерфейса (ТЗ §14.3). Общий и для клиента, и для будущего кабинета бизнеса:
 * второй провайдер завёл бы два независимых переключателя, и на одном экране можно было бы
 * увидеть русскую нижнюю навигацию под узбекским заголовком.
 */
export interface I18nValue {
  readonly lang: Lang;
  setLang(lang: Lang): void;
  /** Строка по ключу. Второй аргумент — подстановки `{name}`. */
  t(key: UiKey, params?: Record<string, string | number>): string;
  /** Ключ ошибки бэкенда → текст. Неизвестный ключ виден в тексте (см. i18n/errors.ts). */
  describeError(failure: ApiFailureLike): DescribedError;
}

const I18nContext = createContext<I18nValue | null>(null);

const STORAGE_KEY = 'app.lang';

export const SUPPORTED_LANGS: readonly Lang[] = ['ru', 'uz'];

function isLang(value: unknown): value is Lang {
  return typeof value === 'string' && (SUPPORTED_LANGS as readonly string[]).includes(value);
}

/**
 * Стартовый язык.
 *
 * Порядок: выбор пользователя → язык Telegram → русский. Русский последним, а не первым,
 * потому что узбекоязычный клиент иначе получал бы русский интерфейс на каждом запуске,
 * пока не найдёт переключатель.
 *
 * `language_code` Telegram даёт коды вида `uz`, `ru`, `en`. Всё, что не `uz`, ведёт на
 * русский: третьей локали в продукте нет (ТЗ §4), и показать англоязычному клиенту
 * пустые строки хуже, чем русские.
 */
export function initialLang(stored: string | null, telegramLanguageCode?: string): Lang {
  if (isLang(stored)) return stored;
  if (telegramLanguageCode?.toLowerCase().startsWith('uz')) return 'uz';
  return 'ru';
}

function readStoredLang(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // Приватный режим и заблокированные cookie роняют доступ к localStorage. Язык —
    // не состояние домена, потеря выбора между запусками ничего не ломает.
    return null;
  }
}

export function I18nProvider(props: {
  children: ReactNode;
  telegramLanguageCode?: string | undefined;
}): JSX.Element {
  const [lang, setLangState] = useState<Lang>(() =>
    initialLang(readStoredLang(), props.telegramLanguageCode),
  );

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // См. readStoredLang: выбор просто не переживёт перезапуск.
    }
    document.documentElement.lang = next;
  }, []);

  const value = useMemo<I18nValue>(
    () => ({
      lang,
      setLang,
      t: (key, params) => (params ? translateWith(lang, key, params) : translate(lang, key)),
      describeError: (failure) => describeApiError(lang, failure),
    }),
    [lang, setLang],
  );

  return <I18nContext.Provider value={value}>{props.children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  // Не подставляем русский по умолчанию: компонент вне провайдера — дефект сборки дерева,
  // и молчаливая работа с захардкоженной локалью спрятала бы его до релиза.
  if (!value) throw new Error('useI18n вызван вне <I18nProvider>');
  return value;
}
